import * as React from "react";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import styled, { css } from "styled-components";
import { OrderedSet } from "immutable";
import UserNotification from "util/UserNotification";
import { Nav, NavItem, MenuItem, Button } from "components/bootstrap";
import { Icon } from "components/common";
import QueryTitle from "views/components/queries/QueryTitle";
import AdaptableQueryTabsConfiguration from "views/components/AdaptableQueryTabsConfiguration";
import CopyToDashboardForm from "views/components/widgets/CopyToDashboardForm";
import View from "views/logic/views/View";
import Search from "views/logic/search/Search";
import CopyPageToDashboard from "views/logic/views/CopyPageToDashboard";
import { loadAsDashboard, loadDashboard } from "views/logic/views/Actions";
import createSearch from "views/logic/slices/createSearch";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { selectView, selectActiveQuery } from "views/logic/slices/viewSelectors";
import fetchSearch from "views/logic/views/fetchSearch";
import useHistory from "routing/useHistory";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useCurrentQueryId from "views/logic/queries/useCurrentQueryId";
import useView from "views/hooks/useView";
import useIsNew from "views/hooks/useIsNew";
import { updateView, getView } from "views/api/views";
import { MoreActionsMenu } from "components/common/MoreActions";
const CLASS_HIDDEN = "hidden";
const CLASS_LOCKED = "locked";
const CLASS_ACTIVE = "active";
const NAV_PADDING = 15;
const TAB_MENU_ITEM_CLASS = "tab-menu-item";
const MORE_TABS_BUTTON_CLASS = "query-tabs-more";
const MORE_TABS_LI_CLASS = "query-tabs-more-li";
const NEW_TAB_BUTTON_CLASS = "query-tab-create";
const tabButtonStyles = css`
  height: 100%;
  width: auto;
  padding: 10px 15px;
  // same color as IconButton
  color: ${({ theme }) => theme.colors.gray[60]};
`;
const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .query-config-btn {
    ${tabButtonStyles}
  }
`;
const StyledQueryNav = styled(Nav)(
  ({ theme }) => css`
    &.nav.nav-tabs {
      border-bottom: 0;
      display: flex;
      white-space: nowrap;
      position: relative;
      padding-left: ${NAV_PADDING}px;

      > li {
        > a {
          color: ${theme.colors.text.primary};
          border: none;

          &:hover,
          &:active,
          &:focus {
            transition: color 150ms ease-in-out;
            background: transparent;
            color: ${theme.colors.variant.dark.primary};
          }
        }
      }

      > li.${MORE_TABS_LI_CLASS} > button,
      > li.${NEW_TAB_BUTTON_CLASS} > button {
        ${tabButtonStyles}
      }

      > li.active {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: -3px;

        > a {
          padding: 9px 15px;
          border: 1px solid ${theme.colors.variant.lighter.default};
          border-bottom: none;
          background-color: ${theme.colors.global.contentBackground};
          color: ${theme.colors.variant.darkest.primary};

          &:hover,
          &:active,
          &:focus {
            border: 1px solid ${theme.colors.variant.lighter.default};
            border-bottom: none;
            color: ${theme.colors.variant.darkest.primary};
          }
        }
      }
    }
  `
);
const QueryTab = styled(NavItem)`
  &&&&.active > a {
    padding: 6px 15px 9px;
  }
`;
const NewTabLi = ({ onClick }) => /* @__PURE__ */ React.createElement("li", { className: NEW_TAB_BUTTON_CLASS }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "transparent", title: "Create New Page", onClick }, /* @__PURE__ */ React.createElement(Icon, { name: "add" })));
const MoreTabsLi = ({ menuItems }) => /* @__PURE__ */ React.createElement("li", { className: MORE_TABS_LI_CLASS }, /* @__PURE__ */ React.createElement(
  MoreActionsMenu,
  {
    className: MORE_TABS_BUTTON_CLASS,
    id: "query-tabs-more",
    "aria-label": "More Dashboard Pages",
    keepMounted: true,
    pullRight: true
  },
  menuItems.toArray()
));
const adjustTabsVisibility = (maxWidth, lockedTab, setLockedTab, queriesConfigBtn) => {
  var _a;
  const dashboardTabs = document.querySelector("#dashboard-tabs");
  const tabItems = dashboardTabs.querySelectorAll(
    `:scope > li:not(.${MORE_TABS_LI_CLASS}):not(.${NEW_TAB_BUTTON_CLASS})`
  );
  const moreItems = dashboardTabs.querySelectorAll(
    `li.${MORE_TABS_LI_CLASS} [role="menu"] a.${TAB_MENU_ITEM_CLASS}`
  );
  const moreBtn = dashboardTabs.querySelector(`.${MORE_TABS_BUTTON_CLASS}`);
  const newBtn = dashboardTabs.querySelector(`.${NEW_TAB_BUTTON_CLASS}`);
  const hiddenItems = [];
  let buttonsWidth = moreBtn.offsetWidth + newBtn.offsetWidth + ((_a = queriesConfigBtn == null ? void 0 : queriesConfigBtn.offsetWidth) != null ? _a : 0) + NAV_PADDING;
  let topTabsWidth = 0;
  tabItems.forEach((tabItem) => {
    tabItem.classList.remove(CLASS_HIDDEN);
    tabItem.setAttribute("aria-hidden", "false");
    if (lockedTab) {
      const anchor = tabItem.querySelector("a");
      const { tabId } = anchor.dataset;
      if (tabId === lockedTab) {
        buttonsWidth += tabItem.offsetWidth;
      }
    }
  });
  tabItems.forEach((tabItem, idx) => {
    if (!tabItem.classList.contains(CLASS_LOCKED)) {
      topTabsWidth += tabItem.offsetWidth;
      if (maxWidth >= topTabsWidth + buttonsWidth) {
        hiddenItems.splice(idx, 1);
      } else {
        tabItem.classList.add(CLASS_HIDDEN);
        tabItem.setAttribute("aria-hidden", "true");
        hiddenItems.push(idx);
      }
    }
  });
  moreItems.forEach((tabItem, idx) => {
    tabItem.classList.remove(CLASS_HIDDEN);
    tabItem.setAttribute("aria-hidden", "false");
    if (!hiddenItems.includes(idx)) {
      tabItem.classList.add(CLASS_HIDDEN);
      tabItem.setAttribute("aria-hidden", "true");
    } else if (tabItem.classList.contains(CLASS_ACTIVE)) {
      const { tabId } = tabItem.dataset;
      setLockedTab(tabId);
    }
  });
  if (hiddenItems.length && moreBtn.classList.contains(CLASS_HIDDEN)) {
    moreBtn.classList.remove(CLASS_HIDDEN);
  } else if (!hiddenItems.length && !moreBtn.classList.contains(CLASS_HIDDEN)) {
    moreBtn.classList.add(CLASS_HIDDEN);
  }
};
const _updateDashboardWithNewSearch = (dashboard, newSearch) => {
  const newDashboard = dashboard.toBuilder().search(newSearch).build();
  return updateView(newDashboard);
};
const addPageToDashboard = (targetDashboard, activeView, queryId) => async (searchJson) => {
  const search = Search.fromJSON(searchJson);
  const newDashboard = CopyPageToDashboard(queryId, activeView, targetDashboard.toBuilder().search(search).build());
  if (!newDashboard || !newDashboard.search) {
    throw Error("Copying the dashboard page failed.");
  }
  const newQueryId = newDashboard.search.queries.last().id;
  const newSearch = await createSearch(newDashboard.search);
  await _updateDashboardWithNewSearch(newDashboard, newSearch);
  return [newDashboard, newQueryId];
};
const _onCopyToDashboard = (selectedDashboardId) => async (_dispatch, getState) => {
  const view = selectView(getState());
  const queryId = selectActiveQuery(getState());
  const dashboardJson = await getView(selectedDashboardId);
  const targetDashboard = View.fromJSON(dashboardJson);
  return fetchSearch(dashboardJson.search_id).then(addPageToDashboard(targetDashboard, view, queryId));
};
const _onCreateNewDashboard = async (view, queryId, history) => {
  const newDashboard = CopyPageToDashboard(
    queryId,
    view,
    View.create().toBuilder().state({}).type(View.Type.Dashboard).search(Search.create().toBuilder().queries([]).build()).build()
  );
  loadAsDashboard(history, newDashboard);
};
const AdaptableQueryTabs = ({
  maxWidth,
  queries,
  titles,
  onRemove,
  onSelect,
  queryTitleEditModal,
  dashboardId
}) => {
  const view = useView();
  const isNew = useIsNew();
  const activeQueryId = useCurrentQueryId();
  const [lockedTab, setLockedTab] = useState();
  const [showConfigurationModal, setShowConfigurationModal] = useState(false);
  const [showCopyToDashboardModal, setShowCopyToDashboardModal] = useState(false);
  const dispatch = useViewsDispatch();
  const history = useHistory();
  const sendTelemetry = useSendTelemetry();
  const queriesConfigBtn = useRef(null);
  const toggleCopyToDashboardModal = useCallback(() => {
    setShowCopyToDashboardModal((cur) => !cur);
  }, []);
  const onCopyToDashboard = useCallback(
    (selectedDashboardId) => dispatch(_onCopyToDashboard(selectedDashboardId)).then(([newDashboard, newQueryId]) => loadDashboard(history, newDashboard.id, newQueryId)).catch((error) => {
      UserNotification.error(`Copying dashboard page failed with error ${error}`);
    }),
    [dispatch, history]
  );
  const onCreateNewDashboard = () => _onCreateNewDashboard(view, activeQueryId, history);
  const openTitleEditModal = useCallback(
    (activeQueryTitle) => {
      if (queryTitleEditModal) {
        queryTitleEditModal.current.open(activeQueryTitle);
      }
    },
    [queryTitleEditModal]
  );
  const currentTabs = useMemo(() => {
    let navItems = OrderedSet();
    let menuItems = OrderedSet();
    let lockedItems = OrderedSet();
    let queriesList = OrderedSet();
    queries.keySeq().forEach((id, idx) => {
      const title = titles.get(id, `Page#${idx + 1}`);
      const tabTitle = /* @__PURE__ */ React.createElement(
        QueryTitle,
        {
          active: id === activeQueryId,
          id,
          onRemove: () => onRemove(id),
          openEditModal: openTitleEditModal,
          openCopyToDashboardModal: toggleCopyToDashboardModal,
          allowsClosing: queries.size > 1,
          title
        }
      );
      navItems = navItems.add(
        lockedTab === id ? null : /* @__PURE__ */ React.createElement(
          QueryTab,
          {
            eventKey: id,
            key: id,
            "data-tab-id": id,
            onClick: () => {
              setLockedTab(void 0);
              onSelect(id);
            }
          },
          tabTitle
        )
      );
      menuItems = menuItems.add(
        lockedTab === id ? null : /* @__PURE__ */ React.createElement(
          MenuItem,
          {
            eventKey: id,
            key: id,
            component: "a",
            className: `${TAB_MENU_ITEM_CLASS} ${activeQueryId === id ? CLASS_ACTIVE : ""}`,
            "data-tab-id": id,
            onClick: () => {
              setLockedTab(id);
              onSelect(id);
            }
          },
          tabTitle
        )
      );
      lockedItems = lockedItems.add(
        lockedTab !== id ? null : /* @__PURE__ */ React.createElement(QueryTab, { eventKey: id, key: id, "data-tab-id": id, onClick: () => onSelect(id), className: CLASS_LOCKED }, tabTitle)
      );
      queriesList = queriesList.add({ id, title });
    });
    return { navItems, menuItems, lockedItems, queriesList };
  }, [queries, titles, activeQueryId, openTitleEditModal, toggleCopyToDashboardModal, lockedTab, onRemove, onSelect]);
  useEffect(() => {
    adjustTabsVisibility(maxWidth, lockedTab, setLockedTab, queriesConfigBtn.current);
  }, [maxWidth, lockedTab, activeQueryId]);
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(StyledQueryNav, { bsStyle: "tabs", activeKey: activeQueryId, id: "dashboard-tabs" }, currentTabs.navItems.toArray(), /* @__PURE__ */ React.createElement(MoreTabsLi, { menuItems: currentTabs.menuItems }), currentTabs.lockedItems.toArray(), /* @__PURE__ */ React.createElement(
    NewTabLi,
    {
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.DASHBOARD_CREATE_PAGE, {
          app_pathname: "dashboard",
          app_section: "dashboard",
          app_action_value: "dashboard-create-page-button"
        });
        onSelect("new");
      }
    }
  )), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "transparent",
      title: "Open pages configuration",
      ref: queriesConfigBtn,
      className: "query-config-btn",
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.DASHBOARD_PAGE_CONFIGURATION, {
          app_pathname: "dashboard",
          app_section: "dashboard",
          app_action_value: "dashboard-page-configuration-button"
        });
        setShowConfigurationModal(true);
      }
    },
    /* @__PURE__ */ React.createElement(Icon, { name: "settings" })
  ), showConfigurationModal && /* @__PURE__ */ React.createElement(
    AdaptableQueryTabsConfiguration,
    {
      show: showConfigurationModal,
      setShow: setShowConfigurationModal,
      dashboardId,
      queriesList: currentTabs.queriesList,
      activeQueryId
    }
  ), showCopyToDashboardModal && /* @__PURE__ */ React.createElement(
    CopyToDashboardForm,
    {
      onCopyToDashboard: (selectedDashboardId) => onCopyToDashboard(selectedDashboardId),
      onCreateNewDashboard: isNew ? void 0 : onCreateNewDashboard,
      onCancel: toggleCopyToDashboardModal,
      activeDashboardId: dashboardId,
      submitButtonText: "Copy page",
      submitLoadingText: "Copying page..."
    }
  ));
};
export default AdaptableQueryTabs;
